import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { ClientRequest, KeycloakAdminApi } from '../../services/keycloak-admin-api';
import { ClientRequestCard } from '../client-request-card/client-request-card';
import { LoadStatus } from '../load-status/load-status';

@Component({
  selector: 'app-client-requests-admin',
  imports: [ClientRequestCard, LoadStatus],
  templateUrl: './client-requests-admin.html',
  styleUrl: './client-requests-admin.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientRequestsAdmin implements OnInit {
  private keycloakApi = inject(KeycloakAdminApi);
  private router = inject(Router);

  isLoading = signal(true);
  loadError = signal<string | null>(null);
  pendingRequests = signal<ClientRequest[]>([]);
  resolvedRequests = signal<ClientRequest[]>([]);

  // Per-row transient UI state, keyed by request id
  editedClientIds = signal<Record<string, string>>({});
  denyReasons = signal<Record<string, string>>({});
  denyModeIds = signal<ReadonlySet<string>>(new Set());
  actionErrors = signal<Record<string, string>>({});
  actionInProgressId = signal<string | null>(null);

  async ngOnInit(): Promise<void> {
    // isRealmAdmin is authoritative from the backend, not derived from JWT roles here
    const detailPermissions = await this.keycloakApi.getMe();
    if (!detailPermissions.isRealmAdmin) {
      await this.router.navigateByUrl('/');
      return;
    }
    await this.loadRequests();
  }

  private async loadRequests(): Promise<void> {
    this.isLoading.set(true);
    this.loadError.set(null);
    try {
      const all = await this.keycloakApi.getAllClientRequests();
      // Pending is a queue (oldest first); resolved is history (most recently reviewed first)
      this.pendingRequests.set(
        all
          .filter((r) => r.status === 'PENDING' || r.status === 'APPROVING')
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      );
      this.resolvedRequests.set(
        all
          .filter((r) => r.status !== 'PENDING' && r.status !== 'APPROVING')
          .sort((a, b) => (b.reviewedAt ?? b.createdAt).localeCompare(a.reviewedAt ?? a.createdAt))
      );
      this.editedClientIds.set(
        Object.fromEntries(this.pendingRequests().map((r) => [r.id, r.requestedClientId]))
      );
    } catch (error) {
      this.loadError.set(error instanceof Error ? error.message : 'Failed to load client requests');
    } finally {
      this.isLoading.set(false);
    }
  }

  isActionDisabled(request: ClientRequest): boolean {
    return request.status === 'APPROVING' || this.actionInProgressId() === request.id;
  }

  getEditedClientId(request: ClientRequest): string {
    return this.editedClientIds()[request.id] ?? request.requestedClientId;
  }

  setEditedClientId(request: ClientRequest, value: string): void {
    this.editedClientIds.update((ids) => ({ ...ids, [request.id]: value }));
  }

  isDenyMode(request: ClientRequest): boolean {
    return this.denyModeIds().has(request.id);
  }

  toggleDenyMode(request: ClientRequest): void {
    this.denyModeIds.update((ids) => {
      const next = new Set(ids);
      if (!next.delete(request.id)) {
        next.add(request.id);
      }
      return next;
    });
  }

  getDenyReason(request: ClientRequest): string {
    return this.denyReasons()[request.id] ?? '';
  }

  setDenyReason(request: ClientRequest, value: string): void {
    this.denyReasons.update((reasons) => ({ ...reasons, [request.id]: value }));
  }

  getActionError(request: ClientRequest): string | undefined {
    return this.actionErrors()[request.id];
  }

  private setActionError(request: ClientRequest, message: string | null): void {
    this.actionErrors.update((errors) => {
      const { [request.id]: _removed, ...rest } = errors;
      return message === null ? rest : { ...rest, [request.id]: message };
    });
  }

  async approve(request: ClientRequest): Promise<void> {
    this.setActionError(request, null);
    this.actionInProgressId.set(request.id);
    try {
      await this.keycloakApi.approveClientRequest(request.id, {
        clientId: this.getEditedClientId(request),
      });
      await this.loadRequests();
    } catch (error) {
      this.setActionError(
        request,
        error instanceof Error ? error.message : 'Failed to approve request'
      );
    } finally {
      this.actionInProgressId.set(null);
    }
  }

  async deny(request: ClientRequest): Promise<void> {
    const reason = this.getDenyReason(request).trim();
    if (!reason) {
      this.setActionError(request, 'A reason is required to deny a request');
      return;
    }

    this.setActionError(request, null);
    this.actionInProgressId.set(request.id);
    try {
      await this.keycloakApi.denyClientRequest(request.id, reason);
      await this.loadRequests();
    } catch (error) {
      this.setActionError(
        request,
        error instanceof Error ? error.message : 'Failed to deny request'
      );
    } finally {
      this.actionInProgressId.set(null);
    }
  }
}
